import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@ibsys/kit';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { RenessansTrendDurationItem } from '@/entities/renessans-dashboards';
import { formatDayPeriod, formatMMSS } from './utils';

type Props = {
  items: RenessansTrendDurationItem[];
};

type TooltipPayloadItem = {
  payload?: RenessansTrendDurationItem;
};

type DurationTooltipProps = {
  active?: boolean;
  payload?: TooltipPayloadItem[];
};

const DurationTooltip: FC<DurationTooltipProps> = ({ active, payload }) => {
  if (!active || !payload || payload.length === 0) return null;
  const point = payload[0]?.payload;
  if (!point) return null;
  return (
    <div className="rounded-md border bg-popover text-popover-foreground px-3 py-2 text-xs shadow">
      <div className="font-medium">{point.period}</div>
      <div>{formatMMSS(point.avgDuration)}</div>
    </div>
  );
};

/**
 * Виджет 2 «Динамика среднего времени разговора»: линейный график средней
 * длительности по периодам. Подписи оси Y и тултип — в формате `мм:сс`.
 * Точки с `avgDuration === null` не рендерятся (разрыв линии).
 */
export const AvgDurationChart: FC<Props> = ({ items }) => {
  const { t } = useTranslation('renessansDashboard');

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('charts.avgDurationTrend')}</CardTitle>
      </CardHeader>
      <CardContent>
        {items.length === 0 && <div className="text-muted-foreground text-sm">{t('noData')}</div>}
        {items.length > 0 && (
          <div className="h-40 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={items} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
                <CartesianGrid stroke="var(--color-border)" strokeDasharray="3 3" />
                <XAxis
                  dataKey="period"
                  stroke="var(--color-muted-foreground)"
                  tick={{ fontSize: 12 }}
                  tickLine={false}
                  tickFormatter={formatDayPeriod}
                />
                <YAxis
                  stroke="var(--color-muted-foreground)"
                  tick={{ fontSize: 12 }}
                  tickLine={false}
                  tickFormatter={(value: number) => formatMMSS(value)}
                  width={56}
                />
                <Tooltip content={<DurationTooltip />} cursor={{ stroke: 'var(--color-muted)' }} />
                <Line
                  type="monotone"
                  dataKey="avgDuration"
                  stroke="var(--color-primary)"
                  strokeWidth={2}
                  dot={{ r: 3, fill: 'var(--color-primary)' }}
                  activeDot={{ r: 5 }}
                  connectNulls={false}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
